import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";
import { useMerchant } from "@auth";
import { buildApiQuery } from "@utils";

/**
 * Wraps useQuery for ServerTable-style paginated queries.
 *
 * Caller must wrap getQueryParams in useCallback with a correct dep list.
 * The cache key uses the buildApiQuery output string, so even if the params
 * object identity changes per render, cache hits still work when the
 * underlying values are stable.
 *
 * The active merchantId is folded into the cache key so switching merchants
 * doesn't serve the previous merchant's cached data. Broad invalidations
 * (e.g. invalidateQueries({ queryKey: ["customers"] })) still match via
 * prefix, so most call sites don't need to know about it.
 *
 * - resource: string segment of the cache key, e.g. "customers"
 * - fetcher: (queryString, { signal }) => Promise<{ data, rows, statistics }>
 * - getQueryParams: () => paramsObject — wrap in useCallback
 * - enabled: gate the query (e.g. recommendation-flow guards)
 */
const useTableQuery = ({
  resource,
  fetcher,
  getQueryParams,
  enabled = true,
}) => {
  const { merchant } = useMerchant();
  const merchantId = merchant?.id;

  const queryString = useMemo(
    () => buildApiQuery(getQueryParams()),
    [getQueryParams],
  );

  return useQuery({
    queryKey: [resource, merchantId, queryString],
    queryFn: ({ signal }) => fetcher(queryString, { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
};

export default useTableQuery;
